<template>
  <div>
    <!-- Policy table -->
    <div class="card pagination-margin" qid="activities-section">
      <div class="card-body">
        <div class="row">
          <div class="col-sm-6">
            <div class="float-left">
              <h5 class="mb-2">{{ $t('maintenance.policies.policy_table_title') }}</h5>
              <span class="cursor-pointer task-label-open mr-2"
                    v-if="getTask('update_relations')"
                    @click="openToolbar(getTask('update_relations'))"
              >
                <font-awesome-icon icon="info-circle" class="text-primary"/>
                {{ $t('tasks.open_label') }}
              </span>
              <span v-if="getGuide('update_relations')">
                <GuideButton :guide="getGuide('update_relations')"></GuideButton>
              </span>
            </div>
          </div>
          <div class="col-sm-6 text-right">
            <button class="btn btn-success ml-3 pl-3 pr-3"
                    @click="showCreateSModal()"
                    v-if="isGranted([permissions.POLICY_EDIT, permissions.POLICY_EDIT_OWN], object) && (!getClient.parent || (getClient.parent && object.partition))"
                    qid="measure-system-create-button"
            >
              <font-awesome-icon icon="plus"/> {{ $t('maintenance.policies.add_button') }}
            </button>
          </div>
        </div>
        <table class="table table-responsive-md table-list-secondary">
          <thead>
          <tr>
            <th scope="col">
              {{ $t('activities.table.name') }}
            </th>
            <th scope="col">
              {{ $t('maintenance.policies.table.category') }}
            </th>
            <th scope="col">
              {{ $t('maintenance.policies.table.level') }}
            </th>
            <th scope="col" class="text-center">
              {{ $t('maintenance.policies.table.subscribe') }}
            </th>
            <th scope="col">
              {{ $t('system.last_updated') }}
            </th>
            <th width="130"></th>
          </tr>
          </thead>
          <tbody>
            <tr v-for="(policy, index) in tableItems">
              <b-tooltip
                  ref="tooltip"
                  v-if="policy.subscribe"
                  :target="'delete-' + index"
              >
                {{ $t('maintenance.policies.table.delete_tooltip') }}
              </b-tooltip>
              <td>
                <router-link
                    :to="{ name: 'policies.view', params: { slug: policy.related_policy.slug, client: policy.related_policy.client }}"
                    qid="s-view-page-link"
                    v-if="policy.related_policy.client == getClient.slug && isOwner(policy.related_policy)"
                >
                  {{ policy.related_policy.name }}
                </router-link>
                <button v-if="policy.related_policy.client != getClient.slug && isOwner(policy.related_policy)"
                        class="btn btn-link btn-sm"
                        @click="openNoticeModal(policy.related_policy)"
                >
                  {{ policy.related_policy.name }}
                </button>
                <span v-if="!isOwner(policy.related_policy)">
                  {{ policy.related_policy.name }}
                </span>
              </td>
              <td>
                <span qid="policy-category"
                      :title="$t('maintenance.policies.category.' + $options.filters.slugify(policy.related_policy.category))"
                      v-if="policy.related_policy.category"
                      class="text-normal"
                >
                  <font-awesome-icon icon="shapes"/>
                  {{ $t('maintenance.policies.category.' + $options.filters.slugify(policy.related_policy.category)) }}
                </span>
              </td>
              <td>

                <span v-if="object.category == 'policy'" class="text-normal">
                  <span v-if="policy.related_policy.category == 'policy'">
                    <font-awesome-icon icon="arrows-alt-h"/>
                    {{ $t('maintenance.policies.level.same') }}
                  </span>

                  <span v-if="policy.related_policy.category == 'procedure'">
                    <font-awesome-icon icon="arrow-alt-from-top"/>
                    {{ $t('maintenance.policies.level.bellow') }}
                  </span>

                  <span v-if="policy.related_policy.category == 'supporting_document'">
                    <font-awesome-icon icon="arrow-alt-from-top"/>
                    {{ $t('maintenance.policies.level.bellow') }}
                  </span>
                </span>

                <span v-if="object.category == 'procedure'" class="text-normal">
                  <span v-if="policy.related_policy.category == 'policy'">
                    <font-awesome-icon icon="arrow-alt-to-bottom"/>
                    {{ $t('maintenance.policies.level.above') }}
                  </span>

                  <span v-if="policy.related_policy.category == 'procedure'">
                    <font-awesome-icon icon="arrows-alt-h"/>
                    {{ $t('maintenance.policies.level.same') }}
                  </span>

                  <span v-if="policy.related_policy.category == 'supporting_document'">
                    <font-awesome-icon icon="arrow-alt-from-top"/>
                    {{ $t('maintenance.policies.level.bellow') }}
                  </span>
                </span>

                <span v-if="object.category == 'supporting_document'" class="text-normal">
                  <span v-if="policy.related_policy.category == 'policy'">
                    <font-awesome-icon icon="arrow-alt-to-bottom"/>
                    {{ $t('maintenance.policies.level.above') }}
                  </span>

                  <span v-if="policy.related_policy.category == 'procedure'">
                    <font-awesome-icon icon="arrow-alt-to-bottom"/>
                    {{ $t('maintenance.policies.level.above') }}
                  </span>

                  <span v-if="policy.related_policy.category == 'supporting_document'">
                    <font-awesome-icon icon="arrows-alt-h"/>
                    {{ $t('maintenance.policies.level.same') }}
                  </span>
                </span>
              </td>
              <td class="text-center">
                <b-form-checkbox
                    :value="1"
                    :unchecked-value="0"
                    :disabled="!(isGranted([permissions.POLICY_EDIT, permissions.POLICY_EDIT_OWN], object) && (!getClient.parent || (getClient.parent && object.partition)))"
                    v-model="policy.notifications"
                    @change="updateNotification(policy)"
                    switch
                    class="icon-success"
                    :title="$t('maintenance.policies.table.notification_tooltip')"
                    v-b-tooltip.hover
                ></b-form-checkbox>
              </td>
              <td>
                <span class="text-normal">
                  {{ policy.related_policy.updated_at | formatDate }}
                </span>
              </td>
              <td>
                <span @click="openViewPolicyModal(policy.related_policy)"
                      class="btn btn-view"
                >
                  <font-awesome-icon icon="eye"/>
                </span>
                <span :id="'delete-' + index" class="pt-3">
                  <button v-if="isGranted([permissions.POLICY_EDIT, permissions.POLICY_EDIT_OWN], object) && (!getClient.parent || (getClient.parent && object.partition))"
                          class="btn btn-delete"
                          qid="delete-p-modal"
                          @click="showConfirmDeleteModal(policy.related_policy)"
                          :disabled="policy.subscribe"
                  >
                    <font-awesome-icon icon="trash-alt"/>
                  </button>
                </span>
              </td>
            </tr>
            <tr v-if="!loading && !tableItems.length" qid="no-results-row" class="text-center">
              <td colspan="10">
                <h5 class="mt-3">
                  {{ $t('system.no_results') }}
                </h5>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="card-body">
        <div class="pagination row">
          <div class="col-md-6">
            <div class="row">
              <div class="float-left">
                <span class="text-muted pagination-counter">
                  {{ $t('pagination.entries_per_page') }}:
                </span>
              </div>
              <div class="float-left">
                <multiselect
                    v-model="perPage"
                    :options="perPageOptions"
                    track-by="value"
                    label="value"
                    :select-label="$t('system.dropdown_select_label')"
                    :selected-label="$t('system.dropdown_selected_label')"
                    :deselect-label="$t('system.dropdown_deselect_label')"
                    qid="create-org-form-organization-level"
                    @select="perPageChanged"
                    :placeholder="selectedPerPage"
                    @remove="perPageRemoved"
                ></multiselect>
              </div>
            </div>
          </div>
          <div class="col-md-6">
            <b-pagination
                class="float-right"
                v-model="currentPage"
                :total-rows="rows"
                :per-page="selectedPerPage"
                aria-controls="my-table"
                @change="pageChanged"
                limit="1"
            >
              <span class="text-muted" slot="first-text"><font-awesome-icon icon="angle-double-left"/></span>
              <span class="text-muted" slot="prev-text"><font-awesome-icon icon="angle-left"/></span>
              <span class="text-muted" slot="next-text"><font-awesome-icon icon="angle-right"/></span>
              <span class="text-muted" slot="last-text"><font-awesome-icon icon="angle-double-right"/></span>
              <div slot="ellipsis-text">
                <b-spinner small type="grow"></b-spinner>
                <b-spinner small type="grow"></b-spinner>
                <b-spinner small type="grow"></b-spinner>
              </div>
              <span class="text-muted" slot="page" slot-scope="{ page, active }">
                <span v-if="active">{{getPerPage}}</span>
              </span>
            </b-pagination>
          </div>
        </div>
      </div>
    </div>


    <!-- Add new policy modal -->
    <b-modal ref="add-process" hide-footer @hide="hideCreateSModal">
      <div class="d-block text-center">
        <h4 qid="add-ac-title">
          {{ $t('maintenance.policies.create_modal_title') }}
        </h4>
      </div>
      <b-alert v-if="error.length"
               show
               variant="danger"
               qid="create-ac-error"
      >
        {{ error }}
      </b-alert>
      <b-form @submit="sFormSubmit" qid="create-ac-form">
        <div class="form-container">
          <b-form-group>
            <label qid="create-ac-pg-label">
              {{ $t('maintenance.policies.policy_label') }}
            </label>
            <multiselect
                v-model="selectedP"
                :options="pItems"
                :placeholder="$t('maintenance.policies.policy_placeholder')"
                label="name"
                :select-label="$t('system.dropdown_select_label')"
                :selected-label="$t('system.dropdown_selected_label')"
                :deselect-label="$t('system.dropdown_deselect_label')"
                @search-change="loadS"
                track-by="slug"
                qid="create-ac-form-ac"
            ></multiselect>
          </b-form-group>
          <div v-if="selectedP && selectedP.description"
               class="text-center mb-3 mt-4"
          >
            <span class="cursor-pointer">
              <span class="mr-2"
                    @click="showSDescription = !showSDescription"
                    qid="expand-process-description"
              >
                {{$t('activities.process.create.process_description_expand')}}
              </span>
              <font-awesome-icon icon="chevron-down" v-if="!showSDescription"/>
              <font-awesome-icon icon="chevron-up" v-if="showSDescription"/>
            </span>
          </div>
          <div v-if="selectedP && selectedP.description && showSDescription" class="mb-4 mt-2">
            <span class="text-formatted">
              {{ selectedP.description }}
            </span>
          </div>
          <div v-if="selectedP && selectedP.tags_translated && selectedP.tags_translated.length">
            <label class="mt-3">
                {{ $t('activities.process.create.tags_label') }}
            </label>
            <br>
            <span class="mr-1" v-for="tag in selectedP.tags_translated" qid="p-tags-list">
              <span class="badge badge-info mb-1" :title="tag.name_translated">
                {{tag.name_translated | truncate(35)}}
              </span>
            </span>
          </div>
        </div>
        <div class="text-right">
          <b-button class="mt-3"
                    variant="secondary"
                    @click="hideCreateSModal"
                    qid="create-ac-cancel-button"
          >
            {{ $t('activities.process.create.cancel') }}
          </b-button>
          <b-button type="submit"
                    class="mt-3 ml-3"
                    :disabled="buttonDisabled"
                    variant="success"
                    qid="create-ac-submit-button"
          >
            <span v-if="buttonDisabled" class="mr-1">
              <font-awesome-icon icon="spinner" size="sm" class="fa-spin"/>
            </span>
            {{ $t('activities.process.create.submit') }}
          </b-button>
        </div>
      </b-form>
    </b-modal>

    <!-- Delete policy confirm modal template -->
    <b-modal ref="delete-policy" hide-footer>
      <div class="d-block text-center">
        <h4 qid="delete-tp-title">
          {{ $t('maintenance.policies.delete.title') }}
        </h4>
      </div>
      <h5 class="text-center">
        {{ $t('maintenance.policies.delete.description') }} {{ itemToDelete.name }}
      </h5>
      <h5 class="text-center">
        {{ $t('maintenance.policies.delete.are_you_sure') }}
      </h5>
      <div class="text-center">
        <b-button class="mt-3"
                  variant="secondary"
                  @click="hideConfirmDeleteModal"
                  qid="delete-tp-cancel-button"
        >
          {{ $t('maintenance.policies.delete.cancel') }}
        </b-button>
        <b-button @click="confirmDeletePolicy(itemToDelete)"
                  class="mt-3 ml-3"
                  variant="success"
                  qid="delete-tp-submit-button"
                  v-if="isGranted([permissions.POLICY_EDIT, permissions.POLICY_EDIT_OWN], object) && (!getClient.parent || (getClient.parent && object.partition))"
        >
          {{ $t('maintenance.policies.delete.submit') }}
        </b-button>
      </div>
    </b-modal>


    <PartitionNoticeModal
        :item="selectedItem"
        state="policies.view"
        :show="showNoticeModal"
        @closeViewModal="closeNoticeModal"
    ></PartitionNoticeModal>

    <PolicyViewModal
        :object="policyToView"
        :showModal="viewPolicyModal"
        @closeViewModal="handleCloseViewPolicyModal"
    ></PolicyViewModal>

  </div>

</template>

<script>
  import piincHttp from '@/util/configureAxios.js'
  import roleCheck from "@/util/ACL/roleCheck"

  import Avatar from '@/components/Avatar.vue';
  import PartitionNoticeModal from '@/components/PartitionNoticeModal.vue';
  import PolicyViewModal from '@/views/Policies/PolicyViewModal.vue';
  import GuideButton from '@/components/guide/GuideButton.vue';


  export default {
    name: 'ActivitiesTable',
    data: function() {
      return {

        // Systems
        tableItems: [],
        selectedS: [],
        viewSModal: false,
        selectedSSort: '',
        selectedSDirection: '',
        pItems: [],
        sLoading: true,

        error: '',
        loadingItems: false,
        itemToDelete: {},
        itemObject: {},
        selectedP: [],
        showSDescription: false,

        currentPage: 1,
        totalRows: 0,
        selectedSort: '',
        selectedDirection: '',
        keywordValue: '',
        myGuides: [],
        buttonDisabled: false,

        loading: true,
        perPage: '',
        selectedPerPage: "10",
        perPageOptions: [
          { name: '10', value: '10' },
          { name: '20', value: '20' },
          { name: '50', value: '50' },
          { name: '100', value: '100' },
        ],
        showNoticeModal: false,
        selectedItem: {},
        policyToView: {},
        viewPolicyModal: false
      }
    },
    props: {
      object: {
        type: Object,
        required: true
      }
    },
    components: {
      Avatar,
      PartitionNoticeModal,
      PolicyViewModal,
      GuideButton
    },
    computed: {
      getClient() {
        return this.$store.state.client
      },
      rows() {
        // Use computed value for rows because of dynamic update
        return this.totalRows
      },
      getUser() {
        return this.$store.state.user
      },
      getStorePerPage() {
        return this.$store.state.perPage
      },
      getPerPage() {
        let page = this.currentPage;
        let selectedFrom = (page-1) * Number(this.selectedPerPage) + 1;
        let selectedTo = (page-1) * Number(this.selectedPerPage) + Number(this.selectedPerPage);
        let selectedOf = this.totalRows;

        if(selectedTo > selectedOf){
          selectedTo = selectedOf
        }

        return selectedFrom + " - " + selectedTo + " " + this.$t('system.of') + " " + selectedOf
      }
    },
    watch: {
      // Watch when prop changes and then load validations
      object: function (value) {
        //this.loadContent()
      }
    },
    methods: {
      getTask(code) {
        let existingTask = _.find(this.tasks, function(object) {
          return object.code == code
        });
        return existingTask
      },
      isOwner(object) {
        let self = this;
        let allow = false;
        let existingUser = _.find(object.users, function(user) {
          return user.id == self.getUser.id
        });

        if (existingUser) {
          allow = true
        } else {
          if (roleCheck('Client admin') || roleCheck('Partition admin') || roleCheck('Validator') || this.getUser.isAdmin) {
            allow = true
          }
        }

        return allow
      },
      openViewPolicyModal(object) {
        this.policyToView = object
        this.viewPolicyModal = true
      },
      handleCloseViewPolicyModal () {
        this.policyToView = []
        this.viewPolicyModal = false;
      },
      openNoticeModal(object) {
        this.selectedItem = object
        this.showNoticeModal = true
      },
      closeNoticeModal() {
        this.selectedItem = {}
        this.showNoticeModal = false
      },
      openToolbar(object) {
        this.$store.commit('storeTaskToolbarState', true);
        this.$store.commit('storeActiveTask', object);
      },
      getGuide(code) {
        let existingGuide = _.find(this.myGuides, function(object) {
          return object.scope == code && object.type == 'Policy' && object.enabled
        });
        return existingGuide
      },
      loadGuides(code) {
        let self = this;
        let language = localStorage.getItem('lang')? localStorage.getItem('lang') : 'en';

        piincHttp.get('guides', { params:
          {
            per_page: 1,
            language: 'en',
            type: 'Policy',
            scope: code,
            client: this.getClient.slug
          }
        }).then(function(response) {
          if (response.data.items.length) {
            self.myGuides.push(response.data.items[0])
          }
        }, function() {});
      },
      showConfirmDeleteModal(object) {
        this.itemToDelete = object
        this.$refs['delete-policy'].show()
      },
      hideConfirmDeleteModal() {
        this.itemToDelete = {}
        this.$refs['delete-policy'].hide()
      },
      openViewSModal(object) {
        this.selectedS = object
        this.viewSModal = true
      },
      handleCloseViewSModal () {
        this.selectedS = []
        this.viewSModal = false;
      },
      pageChanged(page) {
        // Load Third parties when page changed listener is triggered
        this.loadContent(page)
      },
      perPageChanged(perPage) {
        let self = this;

        self.selectedPerPage = perPage.value
        this.$store.commit('storePerPage', this.selectedPerPage);
        // Load Third parties when page changed listener is triggered
        this.loadContent(1, perPage.value)
      },
      perPageRemoved() {
        let self = this;
        // Load Third parties when page changed listener is triggered
        self.selectedPerPage = self.perPageOptions[0].value
        this.loadContent(1, self.selectedPerPage)
      },
      showCreateSModal() {
        this.loadS()
        this.$refs['add-process'].show()
      },
      hideCreateSModal() {
        this.error = ''
        this.selectedP = []
        this.loadContent()
        this.$refs['add-process'].hide()
      },
      sFormSubmit(evt) {
        this.buttonDisabled = true;
        let formData = new FormData();

        // Keep the reference in a variable
        // to use it inside function called when promise returned
        let self = this;
        evt.preventDefault();

        formData.append("related_policy_id", this.selectedP.id);

        piincHttp.post('/policies/' + this.$route.params.slug + '/policies', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }).then(function(response) {
          self.$toastr('success', self.$t('maintenance.policies.create.related_policy_success'))
          self.hideCreateSModal();
          self.loadContent();
          self.buttonDisabled = false;
        }, function(error) {
          if (error.status !== 200) {
            self.buttonDisabled = false;
            if (error.data.errors['related_policy_id']) {
              self.error = self.$t('maintenance.policies.create.' + self.$options.filters.slugify(error.data.errors['related_policy_id'][0]))
            }
          }
        })
      },
      loadS(query) {
        let self = this;
        piincHttp.get('policies', { params:
          {
            client: this.getClient.slug,
            per_page: 100,
            keyword: query ? query : undefined
          }
        }).then(function(response) {
          self.pItems = []
          _.forEach(response.data.items, function (item) {
            if (item.id != self.object.id) {
              self.pItems.push(item)
            }
          })
          if (self.pItems && !self.pItems.length && !query) {
            self.pItems.push({name: self.$t('system.no_results'), $isDisabled: true})
          }
        }, function() {});
      },
      loadContent(page, perPage) {
        let self = this;

        if (this.getStorePerPage) {
          this.selectedPerPage = this.getStorePerPage;
        }

        // If page is not defined, set page to default 1
        let pageNumber = page ? page : 1;
        let perPageNumber = perPage ? perPage : this.selectedPerPage;

        piincHttp.get('policies/' + this.$route.params.slug + '/policies', { params:
          {
            page: pageNumber,
            per_page: perPageNumber,
            client: this.getClient.slug
          }
        }).then(function (response) {
          self.loading = false;
          self.tableItems = response.data.items;
          // Set number of total items
          self.totalRows = response.data.total_count;
        }, function () {
        });

      },
      updateNotification(item) {
        let self = this
        piincHttp.put('/policies/' + this.$route.params.slug + '/policies/' + item.related_policy.slug, {notifications: !item.notifications}).then(function(response) {
          self.loadContent(self.currentPage);
        }, function(error) {
          if (error.status !== 200) {
            self.buttonDisabled = false;
            self.error = error.data.message;
          }
        })
      },
      confirmDeletePolicy(object) {
        let self = this;
        piincHttp.delete('policies/' + this.$route.params.slug + '/policies/' + object.slug).then(function(response) {
          self.hideConfirmDeleteModal();
          self.loadContent(self.currentPage);
          // Trigger toastr message after modal close
          self.$toastr('success', self.$t('maintenance.policies.delete.success'))
        }, function() {});
      },
    },
    created () {
      // Table items
      this.loadContent();
      this.loadGuides('update_relations');
    }
  }
</script>
